import { Box } from '@mui/material';
import { ReactNode, useLayoutEffect, useRef } from 'react';

// A barra do topo do react-admin tem 48px e some ao rolar a página.
const TOP_OFFSET = 64;

const FLOAT_SX = { position: 'fixed', zIndex: 10 };

/**
 * Mockup que fica à vista na aba inteira, ao lado dos campos.
 *
 * `position: sticky` não serve: ele solta o mockup quando a coluna do formulário
 * acaba, e o menu lateral do admin costuma ser mais comprido que a aba — rolar até
 * o fim da página levava o mockup embora. Aqui ele é `fixed`, ancorado num espaço
 * reservado ao lado dos campos: começa alinhado ao topo deles e, ao rolar, para a
 * TOP_OFFSET do topo da janela.
 *
 * A posição é escrita direto no estilo, sem estado: um `setState` por evento de
 * rolagem redesenharia o mockup inteiro a cada pixel.
 */
const FloatingPreview = ({ width, children }: { width: number; children: ReactNode }) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const floatRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const float = floatRef.current;
    if (!anchor || !float) { return; }

    const update = () => {
      // Fora do fluxo, o mockup não ocupa altura: numa aba curta ele passaria por
      // cima da barra do Salvar. A âncora guarda o lugar dele.
      anchor.style.minHeight = `${float.offsetHeight}px`;
      const rect = anchor.getBoundingClientRect();
      float.style.top = `${Math.max(rect.top, TOP_OFFSET)}px`;
      float.style.left = `${rect.left}px`;
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    // A âncora se move sem rolagem: troca de aba (sai do display: none), menu
    // lateral abrindo e fechando, aviso aparecendo acima dos campos. E o mockup muda
    // de altura quando termina de carregar.
    const observer = new ResizeObserver(update);
    observer.observe(anchor);
    observer.observe(float);
    if (anchor.parentElement) { observer.observe(anchor.parentElement); }

    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      observer.disconnect();
    };
  }, []);

  return (
    <Box ref={anchorRef} sx={{ display: { xs: 'none', lg: 'block' }, width, flexShrink: 0 }}>
      <Box ref={floatRef} sx={{ ...FLOAT_SX, width }}>
        {children}
      </Box>
    </Box>
  );
};

export default FloatingPreview;
